import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Lang } from "@/i18n";
import { path } from "@/lib/site";

export type NavLocation = "header" | "footer";
export type NavLinkType = "internal" | "external";

const INTERNAL_KEYS = [
  "home",
  "services",
  "references",
  "about",
  "faq",
  "tools",
  "contact",
  "cookies",
] as const;
type InternalKey = (typeof INTERNAL_KEYS)[number];

function isInternalKey(s: string): s is InternalKey {
  return (INTERNAL_KEYS as readonly string[]).includes(s);
}

const COOKIES_PATH: Record<Lang, string> = {
  fr: "/cookies",
  en: "/en/cookies",
  nl: "/nl/cookies",
};

function resolveInternal(target: string, lang: Lang): string {
  if (target === "cookies") return COOKIES_PATH[lang];
  if (isInternalKey(target) && target !== "cookies") {
    // path() supports the standard PageKey set
    return path(lang, target as Exclude<InternalKey, "cookies">);
  }
  // Treat as a raw path
  return target.startsWith("/") ? target : `/${target}`;
}

const pick = (v: unknown, lang: Lang): string => {
  if (v && typeof v === "object" && lang in (v as object)) {
    const s = (v as Record<string, unknown>)[lang];
    return typeof s === "string" ? s : "";
  }
  return "";
};

export interface ResolvedNavItem {
  id: string;
  label: string;
  href: string;
  isExternal: boolean;
  openInNewTab: boolean;
  groupLabel: string;
  /** Raw target for grouping by internal key when needed */
  target: string;
  linkType: NavLinkType;
}

export function useNavMenu(location: NavLocation, lang: Lang) {
  return useQuery({
    queryKey: ["public", "nav_menus", location, lang],
    queryFn: async (): Promise<ResolvedNavItem[]> => {
      const { data, error } = await supabase
        .from("nav_menus")
        .select("*")
        .eq("location", location)
        .eq("is_visible", true)
        .order("display_order", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((r) => {
        const isExternal = r.link_type === "external";
        const href = isExternal ? r.target : resolveInternal(r.target, lang);
        return {
          id: r.id,
          label: pick(r.label, lang) || pick(r.label, "fr"),
          href,
          isExternal,
          openInNewTab: r.open_in_new_tab,
          groupLabel: pick(r.group_label, lang) || pick(r.group_label, "fr"),
          target: r.target,
          linkType: r.link_type as NavLinkType,
        };
      });
    },
    staleTime: 60_000,
  });
}

export const INTERNAL_TARGET_KEYS = INTERNAL_KEYS;
